import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Send, Bot, User, CheckCircle, Edit, Truck, Calendar, Weight, MapPin, DollarSign } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { formatAppDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from 'react-i18next';
import { useRouteDisplay } from '@/utils/routeUtils';
import { processLocation } from "@/utils/geoStandardization";

interface LoadRequest {
  pickupCity: string;
  dropCity: string;
  pickupDate: string;
  cargoType: string;
  vehicleType: string;
  weightTons: number;
  truckCount: number;
}

interface ParsedLoadRequest {
  loads: LoadRequest[];
  totalTrucks: number;
}

interface ChatMessage {
  type: "bot" | "user";
  content: string;
}

interface LoadRequestChatProps {
  companyName: string;
  onLoadConfirmed: (loadData: any) => void;
  previewMode?: boolean;
  onRequireAuth?: (loadData: any) => void;
  customTitle?: string;
}

export const LoadRequestChat = ({ companyName, onLoadConfirmed, previewMode, onRequireAuth, customTitle }: LoadRequestChatProps) => {
  const { t, i18n } = useTranslation();
  const [message, setMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parsedLoad, setParsedLoad] = useState<ParsedLoadRequest | null>(null);
  const [lastConfirmedLoad, setLastConfirmedLoad] = useState<ParsedLoadRequest | null>(null); // Store load data for editing context
  const [currentLocation, setCurrentLocation] = useState<{city: string, country: string} | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([
    {
      type: "bot",
      content: i18n.language?.startsWith('en')
        ? "Welcome to Shohnat 👋 Describe your loads (single or multiple destinations)."
        : t('loadChat.welcomeMessage')
    }
  ]);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isRTL, language } = useLanguage();
  const { formatRoute, getRouteArrow } = useRouteDisplay();

  // Analytics helper
  const GTAG_ID = 'AW-17456817899';
  const trackEvent = (name: string, params: Record<string, any> = {}) => {
    const gtag = (window as any)?.gtag;
    if (typeof gtag === 'function') {
      try {
        gtag('event', name, {
          send_to: GTAG_ID,
          page_location: window.location.href,
          page_path: `${window.location.pathname}${window.location.search}`,
          language,
          ...params,
        });
      } catch {}
    }
  };

  // Get user's current location
  useEffect(() => {
    const getUserLocation = async () => {
      try {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              // GPS location granted
              const { latitude, longitude } = position.coords;
              
              try {
                const response = await fetch(
                  `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
                );
                const data = await response.json();
                setCurrentLocation({
                  city: data.city || data.locality || 'Unknown',
                  country: data.countryName || 'Unknown'
                });
              } catch (error) {
                console.error('Error getting location details:', error);
                // Fallback to IP location
                await getLocationFromIP();
              }
            },
            async (error) => {
              // GPS permission denied or error, fallback to IP location
              console.log('GPS location denied, using IP location fallback');
              await getLocationFromIP();
            }
          );
        } else {
          // Geolocation not supported, use IP location
          await getLocationFromIP();
        }
      } catch (error) {
        console.error('Error getting user location:', error);
        await getLocationFromIP();
      }
    };

    const getLocationFromIP = async () => {
      try {
        // Using a free IP geolocation service
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        if (data.city && data.country_name) {
          setCurrentLocation({
            city: data.city,
            country: data.country_name
          });
        } else {
          // Fallback to another service if first one fails
          const fallbackResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await fallbackResponse.json();
          
          const locationResponse = await fetch(`https://ipinfo.io/${ipData.ip}/json`);
          const locationData = await locationResponse.json();
          
          setCurrentLocation({
            city: locationData.city || 'Unknown',
            country: locationData.country || 'Unknown'
          });
        }
      } catch (error) {
        console.error('Error getting IP-based location:', error);
        // Set a default location if all methods fail
        setCurrentLocation({
          city: 'Riyadh',
          country: 'Saudi Arabia'
        });
      }
    };

    getUserLocation();
  }, []);

  const callAI = async (userMessage: string, history: ChatMessage[]): Promise<string> => {
    try {
      // Detect user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const { data, error } = await supabase.functions.invoke('chat-ai', {
        body: {
          message: userMessage,
          conversationHistory: history.map(msg => ({
            role: msg.type === 'user' ? 'user' : 'assistant',
            content: msg.content
          })),
          userId: user?.id,
          currentLocation,
          currentLoadData: lastConfirmedLoad, // Pass last confirmed load for editing context
          userTimezone: userTimezone
        }
      });

      if (error) throw error;
      return data.response;
    } catch (error) {
      console.error('AI call failed:', error);
      throw new Error('Failed to get AI response');
    }
  };

  const parseLoadFromAIResponse = (aiResponse: string): ParsedLoadRequest | null => {
    try {
      // Try to parse JSON response from AI (in code blocks)
      const jsonMatch = aiResponse.match(/```json\s*([\s\S]*?)\s*```/);
      console.log('AI Response:', aiResponse);
      console.log('JSON Match:', jsonMatch ? jsonMatch[1] : 'No match');
      
      if (jsonMatch) {
        const jsonStr = jsonMatch[1];
        const parsed = JSON.parse(jsonStr);
        console.log('Parsed JSON:', parsed);
        
        // Handle response that directly includes our format
        if (parsed.loads && Array.isArray(parsed.loads)) {
          return {
            loads: parsed.loads,
            totalTrucks: parsed.totalTrucks || parsed.loads.reduce((sum: number, load: LoadRequest) => sum + load.truckCount, 0)
          };
        }
        
        // Handle standard format with hasCompleteInfo and loads array
        if (parsed.hasCompleteInfo && parsed.loads) {
          return {
            loads: parsed.loads,
            totalTrucks: parsed.loads.reduce((sum: number, load: LoadRequest) => sum + load.truckCount, 0)
          };
        }
        
        // Handle more formats (convert to expected format)
        const load: LoadRequest = {
          pickupCity: parsed.pickupCity || 
                    parsed.pickup_location?.city || 
                    parsed.pickupLocation?.city || "",
          dropCity: parsed.dropCity || 
                  parsed.delivery_location?.city || 
                  parsed.deliveryLocation?.city || "",
          pickupDate: parsed.pickupDate || 
                    parsed.pickup_date || 
                    "",
          cargoType: parsed.cargoType || 
                   parsed.cargo_type || "",
          weightTons: (parsed.weightTons ?? 
                    parsed.weight ?? 
                    parsed.estimated_weight ?? 0),
          truckCount: (parsed.truckCount ?? 
                    parsed.number_of_trucks ?? 
                    parsed.numberOfVehicles ?? 0),
          vehicleType: (parsed.vehicleType || 
                      parsed.vehicle_type || 
                      "").toLowerCase()
        };
        
        return {
          loads: [load],
          totalTrucks: load.truckCount
        };
      }
    } catch (error) {
      console.error('Error parsing AI response:', error);
    }
    
    return null;
  };

  const getDisplayMessage = (aiResponse: string): string => {
    // 1) Strip any fenced code blocks (e.g., ```json ... ```)
    let text = aiResponse.replace(/```[\s\S]*?```/g, '').trim();
    if (text) return text;

    // 2) If JSON appeared without fences, remove it by slicing out the largest {...} block
    const firstBrace = aiResponse.indexOf('{');
    const lastBrace = aiResponse.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      const withoutJson = (aiResponse.slice(0, firstBrace) + aiResponse.slice(lastBrace + 1)).trim();
      if (withoutJson) return withoutJson;
    }

    // 3) Try extracting from the first confirmation marker
    const checkIdx = aiResponse.indexOf('✅');
    if (checkIdx !== -1) {
      return aiResponse.slice(checkIdx).trim();
    }

    // 4) Fallback: return plain trimmed text
    return aiResponse.trim();
};

  const buildFollowUpMessage = (p: ParsedLoadRequest | null): string => {
    if (!p || !p.loads || p.loads.length === 0) {
      return isRTL
        ? 'يرجى تزويدي بمسار التحميل والتفريغ، تاريخ التحميل، نوع الحمولة، نوع المركبة وعدد الشاحنات.'
        : 'Please provide route (pickup and drop cities), pickup date/time, cargo type, vehicle type, and number of trucks.';
    }
    const l = p.loads[0];
    const missing: string[] = [];
    if (!l.dropCity) missing.push(isRTL ? 'مدينة التسليم' : 'drop city');
    if (!l.pickupDate) missing.push(isRTL ? 'تاريخ التحميل' : 'pickup date/time');
    if (!l.cargoType) missing.push(isRTL ? 'نوع الحمولة' : 'cargo type');
    if (!l.vehicleType) missing.push(isRTL ? 'نوع المركبة' : 'vehicle type');
    if (!l.truckCount || l.truckCount <= 0) missing.push(isRTL ? 'عدد الشاحنات' : 'number of trucks');
    const havePickup = l.pickupCity ? (isRTL ? `لدي ${l.pickupCity} كنقطة تحميل.` : `Got pickup: ${l.pickupCity}.`) : '';
    const ask = isRTL
      ? `يرجى تزويدي بـ: ${missing.join('، ')}.`
      : `Please provide: ${missing.join(', ')}.`;
    return `${havePickup} ${ask}`.trim();
  };


  const handleSendMessage = async () => {
    if (!message.trim() || isProcessing) return;

    const userMessage = message;
    setMessage("");
    setIsProcessing(true);

    // Track send-to-chat click
    trackEvent('chat_send_clicked', { message_length: userMessage.length, is_preview: !!previewMode });

    // Add user message to chat
    setChatHistory(prev => [...prev, { type: "user", content: userMessage }]);

    try {
      // Get AI response - pass current parsed load for editing context
      const aiResponse = await callAI(userMessage, chatHistory);
      console.log('=== EDIT DEBUG ===');
      console.log('Full AI Response:', aiResponse);
      console.log('Has JSON match:', /```json\s*([\s\S]*?)\s*```/.test(aiResponse));
      console.log('Current Load Data being sent:', parsedLoad);
      
      // Defer display until after validation

      
      // Try to parse load information from the response
      const parsed = parseLoadFromAIResponse(aiResponse);
      console.log('Parsed Load:', parsed);

      // Safeguard: enforce "two cities + trailing count" shorthand => first city is ALWAYS pickup
      try {
        const normalizeDigits = (s: string) => s.replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString());
        const msgNorm = normalizeDigits(userMessage).trim();
        const patternB = /^\s*([\p{L}][\p{L}\s]+?)\s+([\p{L}][\p{L}\s]+?)\s+(\d+)\s*$/u;
        const m = msgNorm.match(patternB);
        if (m && parsed && parsed.loads?.length === 1) {
          const rawFirst = m[1].trim().replace(/\s+/g, ' ');
          const rawSecond = m[2].trim().replace(/\s+/g, ' ');
          const count = parseInt(m[3], 10);

          // Normalize cities to English tokens (keep cargoType in original language)
          const cityMap: Record<string, string> = {
            'الرياض': 'Riyadh', 'رياض': 'Riyadh', 'riyadh': 'Riyadh',
            'جدة': 'Jeddah', 'جده': 'Jeddah', 'jedda': 'Jeddah', 'jeddah': 'Jeddah',
            'الدمام': 'Dammam', 'دمام': 'Dammam', 'dammam': 'Dammam',
            'مكة': 'Mecca', 'مكه': 'Mecca', 'مكة المكرمة': 'Mecca', 'mecca': 'Mecca',
            'المدينة': 'Medina', 'المدينة المنورة': 'Medina', 'مدينه': 'Medina', 'medina': 'Medina',
            'الخبر': 'Khobar', 'خبر': 'Khobar', 'khobar': 'Khobar',
            'الظهران': 'Dhahran', 'ظهران': 'Dhahran', 'dhahran': 'Dhahran',
            'أبها': 'Abha', 'ابها': 'Abha', 'abha': 'Abha',
            'الطائف': 'Taif', 'طائف': 'Taif', 'taif': 'Taif',
            'بريدة': 'Buraidah', 'بريده': 'Buraidah', 'buraydah': 'Buraidah', 'buraidah': 'Buraidah'
          };
          const normCity = (v: string) => {
            const key = v.toLowerCase();
            const k = key.replace(/^ال/, '');
            return cityMap[key] || cityMap[k] || v;
          };

          const firstCity = normCity(rawFirst);
          const secondCity = normCity(rawSecond);

          const l0 = parsed.loads[0];
          if (l0 && (l0.pickupCity !== firstCity || l0.dropCity !== secondCity)) {
            parsed.loads[0] = {
              ...l0,
              pickupCity: firstCity,
              dropCity: secondCity,
              truckCount: !isNaN(count) ? count : l0.truckCount
            } as typeof l0;
            // Adjust totalTrucks too
            parsed.totalTrucks = parsed.loads.reduce((sum, x) => sum + (x.truckCount || 0), 0);
            console.log('Applied PatternB enforcement, corrected loads (EN cities):', parsed);
          }
        }
      } catch (e) {
        console.log('PatternB enforcement skipped due to error:', e);
      }
      
      // Decide what to display based on completeness
      const isComplete = parsed && parsed.loads?.length > 0 && parsed.loads.every((l) =>
        !!l.pickupCity && !!l.dropCity && !!l.pickupDate && !!l.cargoType && !!l.vehicleType && (l.truckCount ?? 0) > 0
      );
      const displayMessage = getDisplayMessage(aiResponse);
      const messageToShow = isComplete ? displayMessage : buildFollowUpMessage(parsed);
      console.log('Display Message:', messageToShow);
      setChatHistory(prev => [...prev, { type: "bot", content: messageToShow }]);
      
      if (parsed) {
        if (isComplete) {
          console.log('Parsed load complete - showing confirmation card');
          setParsedLoad(parsed);
          setLastConfirmedLoad(parsed); // Store for editing context
        } else {
          console.log('Parsed load incomplete - not showing confirmation card');
        }
      } else {
        console.log('No parsed load - confirmation card will NOT show');
      }
    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      toast({
        title: t('loadChat.errorProcessing'),
        description: t('loadChat.tryAgain'),
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmLoad = async () => {
    if (!parsedLoad || parsedLoad.loads.length === 0 || isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Track confirm-load click
    trackEvent('confirm_load_clicked', {
      total_trucks: parsedLoad.totalTrucks,
      routes: parsedLoad.loads.length,
      is_preview: !!previewMode,
    });

    try {
      console.log('=== LOAD CREATION DEBUG ===');
      console.log('Parsed load data:', JSON.stringify(parsedLoad, null, 2));
      // Dynamic cargo type mapping
      const mapCargoType = (type: string) => {
        if (!type) return 'general';
        
        const lowerType = type.toLowerCase();
        
        // Liquid types
        if (lowerType.includes('water') || lowerType.includes('liquid') || 
            lowerType.includes('fuel') || lowerType.includes('oil') || 
            lowerType.includes('juice') || lowerType.includes('beverage')) return 'liquid';
            
        // Refrigerated types  
        if (lowerType.includes('refrigerat') || lowerType.includes('cold') || 
            lowerType.includes('frozen') || lowerType.includes('ice') || 
            lowerType.includes('dairy') || lowerType.includes('meat')) return 'refrigerated';
            
        // Hazardous types
        if (lowerType.includes('hazard') || lowerType.includes('danger') || 
            lowerType.includes('chemical') || lowerType.includes('toxic') || 
            lowerType.includes('flammable')) return 'hazardous';
            
        // Oversized types
        if (lowerType.includes('oversized') || lowerType.includes('heavy') || 
            lowerType.includes('large') || lowerType.includes('machinery') || 
            lowerType.includes('equipment')) return 'oversized';
            
        // Fragile types
        if (lowerType.includes('fragile') || lowerType.includes('glass') || 
            lowerType.includes('delicate') || lowerType.includes('electronics') || 
            lowerType.includes('breakable')) return 'fragile';
            
        // Default to general for any unrecognized type
        return 'general';
      };
      
      // Dynamic vehicle type mapping
      const mapVehicleType = (type: string) => {
        if (!type) return 'truck';
        
        const lowerType = type.toLowerCase();
        
        // Map various vehicle type variations to database enum values
        if (lowerType.includes('truck') || lowerType === 'truck') return 'truck';
        if (lowerType.includes('van') || lowerType === 'van') return 'van';
        if (lowerType.includes('trailer') || lowerType === 'trailer') return 'trailer';
        
        // Default to truck for any unrecognized type
        return 'truck';
      };

      // Prepare data for creating one load order with multiple routes
      const routes = await Promise.all(parsedLoad.loads.map(async (load) => {
        // Standardize pickup and delivery locations
        const [pickupLocation, deliveryLocation] = await Promise.all([
          processLocation(load.pickupCity),
          processLocation(load.dropCity)
        ]);

        console.log('Pickup location data:', pickupLocation);
        console.log('Delivery location data:', deliveryLocation);

        return {
          title: `${load.pickupCity} to ${load.dropCity}`,
          cargo_desc: load.cargoType || 'General Cargo',
          pickup_address: `${load.pickupCity} Area`,
          // Use exact standardized names from standards tables for consistency
          pickup_city: pickupLocation.standardized_city,
          pickup_state: pickupLocation.standardized_state,
          pickup_country: pickupLocation.standardized_country,
          pickup_lat: pickupLocation.lat,
          pickup_lng: pickupLocation.lng,
          pickup_date: load.pickupDate,
          delivery_address: `${load.dropCity} Area`,
          // Use exact standardized names from standards tables for consistency
          delivery_city: deliveryLocation.standardized_city,
          delivery_state: deliveryLocation.standardized_state,
          delivery_country: deliveryLocation.standardized_country,
          delivery_lat: deliveryLocation.lat,
          delivery_lng: deliveryLocation.lng,
          // Set delivery date to 1 day after pickup date
          delivery_date: load.pickupDate ? new Date(new Date(load.pickupDate).getTime() + 24 * 60 * 60 * 1000).toISOString() : null,
          cargo_type: mapCargoType(load.cargoType || 'general'),
          cargo_weight: load.weightTons ? load.weightTons * 1000 : null, // Convert tons to KG
          vehicles_needed: load.truckCount || 1,
          vehicle_type: mapVehicleType(load.vehicleType)
        };
      }));

      // Calculate aggregated data for the load order
      const totalWeight = routes.reduce((sum, route) => sum + (route.cargo_weight || 0), 0);
      const totalTrucks = routes.reduce((sum, route) => sum + route.vehicles_needed, 0);
      const routeDescriptions = routes.map(r => formatRoute(r.pickup_city, r.delivery_city)).join(', ');
      
      const loadOrderData = {
        title: `Load Request - ${totalTrucks} trucks`,
        description: `Multi-route load: ${routeDescriptions}`,
        cargo_desc: routes.map(r => r.cargo_desc).join(', '),
        cargo_type: routes[0].cargo_type, // Use first route's cargo type as primary
        cargo_weight: totalWeight > 0 ? totalWeight : null,
        vehicles_needed: totalTrucks,
        vehicles_assigned: 0,
        vehicles_active: 0,
        vehicles_completed: 0,
        vehicles_canceled: 0,
        routes: routes
      };
      
console.log('Routes array:', JSON.stringify(routes, null, 2));
console.log('Load order data before sending:', JSON.stringify(loadOrderData, null, 2));

// In preview mode, don't submit. Ask for signup instead.
if (previewMode) {
  console.log('Preview mode enabled - prompting auth instead of submitting');
  onRequireAuth?.(loadOrderData);
  return;
}

// Create the load order with all routes
await onLoadConfirmed(loadOrderData);

// Show success notification immediately
toast({
  title: t('loadChat.loadsConfirmed'),
  description: t('loadChat.carriersNotified'),
  variant: "default"
});

// Navigate immediately without waiting
navigate('/');
      
    } catch (error) {
      console.error('Error confirming load:', error);
      toast({
        title: t('loadChat.error'),
        description: t('loadChat.failedToCreate'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditLoad = () => {
    // Hide confirmation card when editing - it will only show again when AI confirms with new JSON
    setParsedLoad(null);
    // Store the current load data for context before editing
    if (parsedLoad) {
      setLastConfirmedLoad(parsedLoad);
    }
    setChatHistory(prev => [...prev, { 
      type: "bot", 
      content: t('loadChat.whatChanges')
    }]);
  };

  return (
    <div className="w-full sm:max-w-2xl mx-auto px-0 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4">
      <div className="text-center mb-3 sm:mb-6">
        <h2 className="text-lg sm:text-2xl font-bold text-foreground mb-1 sm:mb-2">
          {customTitle ?? (language === 'ar' ? 'تجربة الذكاء الاصطناعي' : t('loadChat.dispatchNewLoad', { companyName }))}
        </h2>
        <p className="text-xs sm:text-base text-muted-foreground">
          {t('loadChat.describeFreight')}
        </p>
      </div>

      {/* Chat Messages */}
      <Card className="shadow-[var(--shadow-card)]">
        <CardContent className="p-2 sm:p-4 space-y-2 sm:space-y-4 max-h-[60vh] sm:max-h-96 overflow-y-auto scroll-smooth">
          {chatHistory.map((msg, index) => (
            <div key={index} className={`flex gap-2 sm:gap-3 ${
              msg.type === 'user' ? 'justify-end' : 'justify-start'
            }`}>
              <div className={`flex gap-2 max-w-[85%] sm:max-w-[80%] ${
                msg.type === 'user' 
                  ? 'flex-row-reverse' 
                  : (isRTL ? 'flex-row-reverse' : 'flex-row')
              }`}>
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.type === 'bot' ? 'bg-primary' : 'bg-secondary'
                }`}>
                  {msg.type === 'bot' ? (
                    <img src="/lovable-uploads/e7c5ffd3-3be2-4ba7-b933-82511564b726.png" alt="AI Assistant" className="h-4 w-4 sm:h-5 sm:w-5" loading="lazy" />
                  ) : (
                    <User className="h-3 w-3 sm:h-4 sm:w-4 text-secondary-foreground" />
                  )}
                </div>
                <div className={`rounded-lg p-2 sm:p-3 text-sm sm:text-base ${
                  msg.type === 'bot' 
                    ? 'bg-muted text-foreground' 
                    : 'bg-primary text-primary-foreground'
                }`}>
                  {msg.content.split('\n').map((line, i) => {
                    const isLastBotMessage = msg.type === 'bot' && index === chatHistory.length - 1;

                    // First line is the main message: show as-is
                    if (i === 0) {
                      return (
                        <span key={i} className="inline-block">
                          {line}
                          {i < msg.content.split('\n').length - 1 && <br />}
                        </span>
                      );
                    }

                    // For confirmation routes, use parsedLoad to render routes consistently
                    if (isLastBotMessage && parsedLoad && i - 1 < parsedLoad.loads.length) {
                      const load = parsedLoad.loads[i - 1];
                      const routeStr = formatRoute(load.pickupCity, load.dropCity);
                      return (
                        <div key={i} className="block mt-3 font-medium">
                          {isRTL ? (
                            <span>{routeStr} x{load.truckCount}</span>
                          ) : (
                            <span>{load.truckCount}x {routeStr}</span>
                          )}
                        </div>
                      );
                    }

                    // Fallback: if parsedLoad is present on last bot message, skip extra original lines
                    if (isLastBotMessage && parsedLoad) {
                      return null;
                    }
                    return (
                      <span key={i} className="inline-block">
                        {line}
                        {i < msg.content.split('\n').length - 1 && <br />}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          {isProcessing && (
            <div className="flex gap-2 sm:gap-3 justify-start">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <Bot className="h-3 w-3 sm:h-4 sm:w-4 text-primary-foreground" />
              </div>
              <div className="bg-muted rounded-lg p-2 sm:p-3">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Parsed Load Confirmation */}
      {parsedLoad && (
        <Card className="shadow-[var(--shadow-card)] border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <CheckCircle className="h-5 w-5" />
              {t('loadChat.loadDetailsParsed')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6 pt-0">
            {/* Summary */}
            <div className="bg-muted/50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">{t('loadChat.totalTrucks')}:</span>
                <span className="font-bold text-primary">{parsedLoad.totalTrucks}</span>
              </div>
            </div>

            {/* Individual Loads */}
            <div className="space-y-3">
              {parsedLoad.loads.map((load, index) => (
                <div key={index} className="border rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{t('loadChat.loadNumber', { number: index + 1 })}</h4>
                    <Badge variant="secondary" className="text-xs">
                      {load.truckCount}{' '}
                      {['truck', 'dyna'].includes((load.vehicleType || '').toLowerCase())
                        ? t(`vehicleTypes.${(load.vehicleType || '').toLowerCase()}`)
                        : `${t('vehicleTypes.truck')} / ${t('vehicleTypes.dyna')}`}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {t('loadChat.route')}
                      </div>
                      <p className="font-medium text-sm">{formatRoute(load.pickupCity, load.dropCity)}</p>
                    </div>
                    
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {t('loadChat.pickupTime')}
                      </div>
                      <p className="font-medium text-sm">{formatAppDate(load.pickupDate, language, (() => { const z = Intl.DateTimeFormat().resolvedOptions().timeZone; return z === 'UTC' ? 'Asia/Riyadh' : z; })())}</p>
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Weight className="h-3 w-3" />
                        {t('loadChat.cargo')}
                      </div>
                      <p className="font-medium text-sm">
                        {load.cargoType}{load.weightTons > 0 ? ` - ${load.weightTons}T` : ''}
                      </p>
                    </div>
                     
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Truck className="h-3 w-3" />
                        {t('loadChat.vehicleType')}
                      </div>
                      <Badge variant="outline" className="text-xs">{load.vehicleType}</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4">
              <Button 
                onClick={handleConfirmLoad} 
                variant="accent" 
                className="flex-1 h-10 sm:h-auto text-sm sm:text-base"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span>{t('loadChat.processing')}</span>
                  </div>
                ) : (
                  <>
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                    <span className="hidden sm:inline">{t('loadChat.confirmSendCarriers')}</span>
                    <span className="sm:hidden">{t('loadChat.confirmSend')}</span>
                  </>
                )}
              </Button>
              <Button 
                onClick={handleEditLoad} 
                variant="outline" 
                className="flex-1 h-10 sm:h-auto text-sm sm:text-base"
                disabled={isSubmitting}
              >
                <Edit className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                {t('loadChat.editDetails')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Message Input */}
      {!parsedLoad && (
        <div className="flex gap-2">
          <Input
            placeholder={t('loadChat.placeholder')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            disabled={isProcessing}
            className="flex-1 text-base min-h-[44px]"
          />
          <Button 
            onClick={handleSendMessage} 
            disabled={isProcessing || !message.trim()}
            className="min-h-[44px] min-w-[44px] sm:w-auto p-2 sm:px-4"
            size="sm"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};